import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../adminApi';
import { showAlert } from '../utils/dialog';

const COURIERS = [
  { key: 'callcourier', name: 'CallCourier', tagline: 'Domestic courier — booking & tracking', color: '#c0392b', textColor: '#fff',    canTest: true  },
  { key: 'postex',      name: 'PostEx',      tagline: 'Express delivery across Pakistan',       color: '#f59e0b', textColor: '#1a1a1a', canTest: false },
  { key: 'tcs',         name: 'TCS',         tagline: 'TCS courier services',                  color: '#dc2626', textColor: '#fff',    canTest: false },
];

const CC_FIELDS = [
  { key: 'shipper_name',     label: 'Business Name',  placeholder: 'Your business name',    required: true },
  { key: 'shipper_cell',     label: 'Cell Number',    placeholder: '03XXXXXXXXX',           required: true },
  { key: 'shipper_landline', label: 'Landline',       placeholder: '021XXXXXXX',            required: true },
  { key: 'shipper_email',    label: 'Email',          placeholder: 'business@email.com',    required: true },
  { key: 'shipper_address',  label: 'Pickup Address', placeholder: 'Full address',          required: true, wide: true },
];

function CCForm({ creds, onChange, cities, areas, loadingCities, loadingAreas }) {
  const [citySearch, setCitySearch] = useState('');
  const [areaSearch, setAreaSearch] = useState('');
  const [cityOpen,   setCityOpen]   = useState(false);
  const [areaOpen,   setAreaOpen]   = useState(false);
  const cityRef = useRef(null);
  const areaRef = useRef(null);

  useEffect(() => {
    const h = e => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setCityOpen(false);
      if (areaRef.current && !areaRef.current.contains(e.target)) setAreaOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const cityLabel = cities.find(c => c.CityId === creds.shipper_city)?.CityName || creds.shipper_city_name || '';
  const areaLabel = areas.find(a => a.AreaId === creds.shipper_area)?.AreaName  || creds.shipper_area_name || '';

  const filteredCities = cities.filter(c => c.CityName.toLowerCase().includes(citySearch.toLowerCase()));
  const filteredAreas  = areas.filter(a  => a.AreaName.toLowerCase().includes(areaSearch.toLowerCase()));

  const pickCity = city => {
    onChange('shipper_city',      city.CityId);
    onChange('shipper_city_name', city.CityName);
    onChange('origin',            city.CityName);
    onChange('shipper_area',      '');
    onChange('shipper_area_name', '');
    setCitySearch(''); setCityOpen(false);
  };
  const pickArea = area => {
    onChange('shipper_area',      area.AreaId);
    onChange('shipper_area_name', area.AreaName);
    setAreaSearch(''); setAreaOpen(false);
  };

  return (
    <div className="cr-body">
      <div className="cr-group">
        <div className="cr-group-title">Account</div>
        <div className="cr-fields">
          <div className="cr-field">
            <label>Login ID <span className="cr-req">*</span></label>
            <input value={creds.login_id || ''} placeholder="Your CallCourier login ID" onChange={e => onChange('login_id', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="cr-group">
        <div className="cr-group-title">Shipper Info</div>
        <div className="cr-fields">

          {/* City */}
          <div className="cr-field" ref={cityRef}>
            <label>City <span className="cr-req">*</span></label>
            <div className="cr-select-wrap">
              <div className={`cr-select-box ${cityOpen ? 'cr-select-open' : ''}`} onClick={() => { setCityOpen(v => !v); setCitySearch(''); }}>
                <span className={cityLabel ? '' : 'cr-select-placeholder'}>
                  {cityLabel || (loadingCities ? 'Loading…' : 'Select city')}
                </span>
                <span className="cr-select-arrow">▾</span>
              </div>
              {cityOpen && (
                <div className="cr-dropdown">
                  <div className="cr-dropdown-search">
                    <input autoFocus placeholder="Search city…" value={citySearch} onChange={e => setCitySearch(e.target.value)} onClick={e => e.stopPropagation()} />
                  </div>
                  <div className="cr-dropdown-list">
                    {filteredCities.length === 0
                      ? <div className="cr-dropdown-empty">No results</div>
                      : filteredCities.map(c => (
                          <div key={c.CityId} className={`cr-dropdown-item ${creds.shipper_city === c.CityId ? 'cr-dropdown-selected' : ''}`} onClick={() => pickCity(c)}>
                            {c.CityName}
                          </div>
                        ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Area */}
          <div className="cr-field" ref={areaRef}>
            <label>Area <span className="cr-req">*</span></label>
            <div className="cr-select-wrap">
              <div
                className={`cr-select-box ${!creds.shipper_city ? 'cr-select-disabled' : ''} ${areaOpen ? 'cr-select-open' : ''}`}
                onClick={() => { if (creds.shipper_city) { setAreaOpen(v => !v); setAreaSearch(''); } }}
              >
                <span className={areaLabel ? '' : 'cr-select-placeholder'}>
                  {!creds.shipper_city ? 'Select city first' : areaLabel || (loadingAreas ? 'Loading…' : 'Select area')}
                </span>
                <span className="cr-select-arrow">▾</span>
              </div>
              {areaOpen && (
                <div className="cr-dropdown">
                  <div className="cr-dropdown-search">
                    <input autoFocus placeholder="Search area…" value={areaSearch} onChange={e => setAreaSearch(e.target.value)} onClick={e => e.stopPropagation()} />
                  </div>
                  <div className="cr-dropdown-list">
                    {filteredAreas.length === 0
                      ? <div className="cr-dropdown-empty">No results</div>
                      : filteredAreas.map(a => (
                          <div key={a.AreaId} className={`cr-dropdown-item ${creds.shipper_area === a.AreaId ? 'cr-dropdown-selected' : ''}`} onClick={() => pickArea(a)}>
                            {a.AreaName}
                          </div>
                        ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="cr-field cr-field-wide">
            <label>Pickup Address <span className="cr-req">*</span></label>
            <input value={creds.shipper_address || ''} placeholder="Full street address" onChange={e => onChange('shipper_address', e.target.value)} />
          </div>

          {/* Other fields */}
          {CC_FIELDS.filter(f => f.key !== 'shipper_address').map(f => (
            <div key={f.key} className={`cr-field ${f.wide ? 'cr-field-wide' : ''}`}>
              <label>{f.label} {f.required && <span className="cr-req">*</span>}</label>
              <input value={creds[f.key] || ''} placeholder={f.placeholder} onChange={e => onChange(f.key, e.target.value)} />
            </div>
          ))}

        </div>
      </div>
    </div>
  );
}

function GenericForm({ courier, creds, onChange }) {
  const fieldMap = {
    postex: [
      { group: 'Account',      items: [
        { key: 'api_key',        label: 'API Key',           placeholder: 'Bearer token / API key', required: true },
        { key: 'pickup_id',      label: 'Pickup Address ID', placeholder: 'PostEx pickup ID' },
      ]},
      { group: 'Shipper Info', items: [
        { key: 'shipper_name',   label: 'Business Name',     placeholder: 'Your business name' },
        { key: 'shipper_phone',  label: 'Phone Number',      placeholder: '03XXXXXXXXX' },
      ]},
    ],
    tcs: [
      { group: 'Account',      items: [
        { key: 'username',       label: 'Username',          placeholder: 'TCS username',    required: true },
        { key: 'password',       label: 'Password',          placeholder: '••••••••',        required: true, type: 'password' },
        { key: 'cost_center',    label: 'Cost Center',       placeholder: 'Cost center code' },
      ]},
      { group: 'Shipper Info', items: [
        { key: 'shipper_name',   label: 'Business Name',     placeholder: 'Your business name' },
        { key: 'shipper_address',label: 'Pickup Address',    placeholder: 'Full address', wide: true },
      ]},
    ],
  };
  return (
    <div className="cr-body">
      {(fieldMap[courier.key] || []).map(group => (
        <div key={group.group} className="cr-group">
          <div className="cr-group-title">{group.group}</div>
          <div className="cr-fields">
            {group.items.map(f => (
              <div key={f.key} className={`cr-field ${f.wide ? 'cr-field-wide' : ''}`}>
                <label>{f.label} {f.required && <span className="cr-req">*</span>}</label>
                <input type={f.type || 'text'} value={creds[f.key] || ''} placeholder={f.placeholder} onChange={e => onChange(f.key, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ccConfigured(creds) {
  return ['login_id','shipper_city','shipper_area','shipper_name','shipper_cell',
          'shipper_address','shipper_landline','shipper_email'].every(k => (creds[k] || '').trim());
}

function AdminCourier() {
  const [active,        setActive]        = useState('callcourier');
  const [settings,      setSettings]      = useState({});
  const [loading,       setLoading]       = useState(true);
  const [creds,         setCreds]         = useState({});
  const [enabled,       setEnabled]       = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [testing,       setTesting]       = useState(false);
  const [testResult,    setTestResult]    = useState(null);
  const [dirty,         setDirty]         = useState(false);

  // Cities/areas live here — fetched once, survive tab switches
  const [cities,        setCities]        = useState([]);
  const [areas,         setAreas]         = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingAreas,  setLoadingAreas]  = useState(false);
  const loadedCityId = useRef(null);

  // Fetch city list once
  useEffect(() => {
    setLoadingCities(true);
    adminApi.getCCCities()
      .then(r => setCities(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingCities(false));
  }, []);

  // Fetch areas when city changes — skip if already loaded for that city
  useEffect(() => {
    const cityId = creds.shipper_city;
    if (!cityId) { setAreas([]); loadedCityId.current = null; return; }
    if (cityId === loadedCityId.current) return;
    loadedCityId.current = cityId;
    setLoadingAreas(true);
    setAreas([]);
    adminApi.getCCAreas(cityId)
      .then(r => setAreas(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingAreas(false));
  }, [creds.shipper_city]);

  const loadSettings = useCallback(() => {
    setLoading(true);
    adminApi.getCourierSettings()
      .then(r => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const settingsLoaded = useRef(false);
  const prevActive     = useRef(null);
  useEffect(() => {
    const hasData = Object.keys(settings).length > 0;
    if (!hasData) return; // wait until DB response arrives
    if (settingsLoaded.current && prevActive.current === active) return; // skip after local save
    settingsLoaded.current = true;
    prevActive.current     = active;
    const s = settings[active];
    setCreds(s?.creds || {});
    setEnabled(s?.is_active || false);
    setTestResult(null);
    setDirty(false);
  }, [active, settings]);

  const courier = COURIERS.find(c => c.key === active);

  const handleChange = (key, val) => {
    setCreds(p => ({ ...p, [key]: val }));
    setTestResult(null);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.saveCourierSettings(active, { creds, is_active: enabled });
      setDirty(false);
      setSettings(prev => ({ ...prev, [active]: { ...prev[active], creds, is_active: enabled } }));
    } catch {
      showAlert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      if (dirty) {
        await adminApi.saveCourierSettings(active, { creds, is_active: enabled });
        setDirty(false);
        setSettings(prev => ({ ...prev, [active]: { ...prev[active], creds, is_active: enabled } }));
      }
      const r = await adminApi.testCourierConnection(active);
      setTestResult(r.data);
    } catch {
      setTestResult({ ok: false, message: 'Request failed' });
    } finally {
      setTesting(false);
    }
  };

  const configured = active === 'callcourier' ? ccConfigured(creds) : true;

  return (
    <div className="cr-page">
      <div className="cr-sidebar">
        <div className="cr-sidebar-title">Couriers</div>
        {COURIERS.map(c => {
          const s    = settings[c.key];
          const ok   = s?.is_active && (c.key === 'callcourier' ? ccConfigured(s?.creds || {}) : true);
          const saved = s && Object.keys(s?.creds || {}).length > 0;
          return (
            <button key={c.key} className={`cr-tab ${active === c.key ? 'cr-tab-active' : ''}`} onClick={() => setActive(c.key)}>
              <div className="cr-tab-logo" style={{ background: c.color, color: c.textColor }}>{c.name[0]}</div>
              <div className="cr-tab-info">
                <span className="cr-tab-name">{c.name}</span>
                <span className={`cr-tab-status ${ok ? 'cr-status-on' : saved ? 'cr-status-idle' : 'cr-status-off'}`}>
                  <span className="cr-dot" />
                  {ok ? 'Connected' : saved ? 'Saved' : 'Not set up'}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="cr-main">
        {loading ? (
          <div className="admin-loading"><div className="admin-spinner" /></div>
        ) : (
          <>
            <div className="cr-header">
              <div className="cr-header-left">
                <div className="cr-header-logo" style={{ background: courier.color, color: courier.textColor }}>{courier.name[0]}</div>
                <div>
                  <div className="cr-header-name">{courier.name}</div>
                  <div className="cr-header-tagline">{courier.tagline}</div>
                </div>
              </div>
              <label className="cr-toggle">
                <input type="checkbox" checked={enabled} onChange={e => { setEnabled(e.target.checked); setDirty(true); }} />
                <span className="cr-toggle-track"><span className="cr-toggle-thumb" /></span>
                <span className="cr-toggle-label">{enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>

            {active === 'callcourier'
              ? <CCForm creds={creds} onChange={handleChange} cities={cities} areas={areas} loadingCities={loadingCities} loadingAreas={loadingAreas} />
              : <GenericForm courier={courier} creds={creds} onChange={handleChange} />
            }

            <div className="cr-footer">
              <div className="cr-footer-left">
                {testResult && (
                  <div className={`cr-test-result ${testResult.ok ? 'cr-test-ok' : 'cr-test-fail'}`}>
                    <span className="cr-test-dot" />{testResult.message}
                  </div>
                )}
              </div>
              <div className="cr-footer-btns">
                {courier.canTest && (
                  <button className="cr-btn-test" onClick={handleTest} disabled={testing || saving || !configured}>
                    {testing ? 'Testing…' : 'Test Connection'}
                  </button>
                )}
                <button className={`cr-btn-save ${!dirty ? 'cr-btn-saved' : ''}`} onClick={handleSave} disabled={saving || !dirty}>
                  {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminCourier;
